/* eslint-disable @typescript-eslint/no-empty-interface */
import './LandingPage.scss';

import {
    Box,
    Button,
    ColumnLayout,
    Container,
    Grid,
    Header,
    Icon,
    IconProps,
    SpaceBetween,
} from '@amzn/awsui-components-react';
import React from 'react';
import { useHistory } from 'react-router-dom';

import RecentPortfoliosList from './components/common/RecentPortfolios';
import { ReleaseSection } from './components/whats-new/WhatsNew';

export const externalLinkProps = {
    external: true,
    externalIconAriaLabel: 'Opens in a new tab'
};

const ExternalLink = (props: { href: string, text: string, className?: string }) => {
    return (
        <a href={props.href} className={props.className} target='_blank'>
            {props.text}
        </a>
    );
};

interface TitleAndDescription {
    title: string;
    description: string;
}

interface TitleDescriptionAndLearnMore extends TitleAndDescription {
    learnMoreLinkLocation: string;
}

interface LinkAttributes {
    linkLocation: string;
    linkText: string;
}

interface FeatureInfo {
    title: string;
    description: JSX.Element;
}

interface UseCaseInfo extends TitleDescriptionAndLearnMore {
}

interface ResourceInfo extends LinkAttributes {
}

interface ServiceInfo extends TitleAndDescription {
    serviceLinkLocation: string;
}

interface LandingProps {
    title: string;
    serviceName: string;
    majorDescription: string;
    minorDescription?: string;
    features: FeatureInfo[];
    whatsNew: ReleaseSection[];
    moreResources: ResourceInfo[];
    useCases: UseCaseInfo[];
    relatedServices: ServiceInfo[];
}

const Title = (props: { title: string }) => {
    return (
        <Box fontWeight="light" padding={{ top: 'xs' }}>
            <span className="custom-home__category">{props.title}</span>
        </Box>
    );
};

const Name = (props: { name: string }) => {
    return (
        <Box variant="h1" fontWeight="bold" padding="n" fontSize="display-l" color="inherit">
            {props.name}
        </Box>
    );
};

const MinorDescription = (props: { text: string }) => {
    return (
        <Box variant="p" fontWeight="light">
            <span className="custom-home__header-sub-title">
                {props.text}
            </span>
        </Box>
    );
};

const MajorDescription = (props: { text: string }) => {
    return (
        <Box fontWeight="light" padding={{ bottom: 's' }} fontSize="display-l" color="inherit">
            {props.text}
        </Box>
    );
};

const AssessmentControls = () => {

    const history = useHistory();

    return (
        <div>
            <SpaceBetween size='l'>
                <Box variant="h2" padding="n">
                    Create and view portfolios
                </Box>
                <SpaceBetween size='s'>
                    <Button href='#' onClick={() => {
                        history.push('/portfolios');
                    }} variant='primary'>Get Started</Button>
                    <RecentPortfoliosList showWhileLoading={false}/>
                </SpaceBetween>
            </SpaceBetween>
        </div>
    );
};


const Features = (props: { features: FeatureInfo[] }) => {
    return (
        <div>
            <Box fontSize="heading-xl" fontWeight="normal" variant="h2">
                Features
            </Box>
            <Container>
                <ColumnLayout columns={2} variant="text-grid">
                    {props.features.map(feature =>
                        <div key={feature.title}>
                            <Box variant="h3" padding={{ top: 'n' }}>
                                {feature.title}
                            </Box>
                            <Box>
                                {feature.description}
                            </Box>
                        </div>)
                    }
                </ColumnLayout>
            </Container>
        </div>
    );
};

const UseCases = (props: { useCases: UseCaseInfo[] }) => {
    return (
        <div>
            <Box fontSize="heading-xl" fontWeight="normal" variant="h2">
                Use cases
            </Box>
            <Container>
                <ColumnLayout columns={2} variant="text-grid">
                    {props.useCases.map(useCase =>
                        <div key={useCase.title}>
                            <Box variant="h3" padding={{ top: 'n' }}>
                                {useCase.title}
                            </Box>
                            {useCase.description ? (
                                <Box variant="p">
                                    {useCase.description}
                                </Box>
                            ) : null}
                            {useCase.learnMoreLinkLocation ? (
                                <ExternalLink href={useCase.learnMoreLinkLocation} text="Learn More"/>
                            ) : null}
                        </div>)}
                </ColumnLayout>
            </Container>
        </div>
    );
};
const RelatedServices = (props: { relatedServices: ServiceInfo[] }) => {
    return (
        <Container header={<Header variant="h2">Related services</Header>}>
            <ColumnLayout columns={2} variant="text-grid">
                {props.relatedServices.map(service =>
                    <div key={service.title}>
                        <Box variant="h3" padding={{ top: 'n' }}>
                            <ExternalLink href={service.serviceLinkLocation} text={service.title}/>
                        </Box>
                        <Box variant="p">{service.description}</Box>
                    </div>
                )}

            </ColumnLayout>
        </Container>
    );
};

const WhatsNew = (props: { newSections: ReleaseSection[] }) => {
    return (
        <Container
            header={
                <Header variant='h2'>
                    What's new?
                </Header>
            }
        >
            {props.newSections.map(section => {
                return (
                    <div key={section.title} className='custom-home__whats-new-row'>
                        <p className='custom-home__whats-new-row-item'><i>{section.title}</i></p>
                        {section.items.map(item => {
                            const iconName: IconProps.Name = item.bug ? 'bug' : 'microphone';
                            return (
                                <p key={item.description} className='custom-home__whats-new-row-item'>
                                    <Icon name={iconName}/>
                                    &nbsp;{item.description}
                                </p>
                            );
                        })}
                    </div>
                );
            })}
            <div className='custom-home__whats-new-row'><a href='/whats-new'>See more ...</a></div>
        </Container>
    );
};

const MoreResources = (props: { moreResources: ResourceInfo[] }) => {
    return (
        <Container
            header={
                <Header variant="h2">
                    More resources <Icon name="external" />
                </Header>
            }
        >
            <ul aria-label="Additional resource links" className="custom-list-separator">
                {props.moreResources.map(r =>
                    <li key={r.linkText}>
                        <ExternalLink href={r.linkLocation} text={r.linkText} />
                    </li>
                )}
            </ul>
        </Container>
    );
};

const LandingPage = (props: LandingProps): JSX.Element => {
    return (
        <Box margin={{ bottom: 'l' }}>
            <div className="custom-home__header">
                <Box padding={{ vertical: 'xxl', horizontal: 's' }}>
                    <Grid
                        gridDefinition={[
                            { offset: { l: 2, xxs: 1 }, colspan: { l: 8, xxs: 10 } },
                            { colspan: { xl: 6, l: 5, s: 6, xxs: 10 }, offset: { l: 2, xxs: 1 } },
                            { colspan: { xl: 2, l: 3, s: 4, xxs: 10 }, offset: { s: 0, xxs: 1 } }
                        ]}
                    >
                        <Title title={props.title} />
                        <div className="custom-home__header-title">
                            <Name name={props.serviceName} />
                            {props.minorDescription ? <MinorDescription text={props.minorDescription} /> : null}
                            <MajorDescription text={props.majorDescription} />
                        </div>
                        <Container>
                            <SpaceBetween size="xl">
                                <AssessmentControls />
                            </SpaceBetween>
                        </Container>
                    </Grid>
                </Box>
            </div>

            <Box margin={{ top: 's' }} padding={{ top: 'xxl', horizontal: 's' }}>
                <Grid
                    gridDefinition={[
                        { colspan: { xl: 6, l: 5, s: 6, xxs: 10 }, offset: { l: 2, xxs: 1 } },
                        { colspan: { xl: 2, l: 3, s: 4, xxs: 10 }, offset: { s: 0, xxs: 1 } }
                    ]}
                >
                    <div className="custom-home-main-content-area">
                        <SpaceBetween size="l">
                            <Features features={props.features} />
                            <UseCases useCases={props.useCases} />
                            <RelatedServices relatedServices={props.relatedServices} />
                        </SpaceBetween>
                    </div>
                    <div className="custom-home__sidebar">
                        <SpaceBetween size="xxl">
                            <WhatsNew newSections={props.whatsNew} />
                            <MoreResources moreResources={props.moreResources} />
                        </SpaceBetween>
                    </div>
                </Grid>
            </Box>
        </Box>
    );
};

export default LandingPage;
